import { useCallback, useEffect, useRef, useState } from "react";
import CoreLayout from "../../components/layout/CoreLayout";
import { type CallerRow, fetchCallers, hydrateNames } from "../../lib/callers";
import { formatDateTime, parseDateTime } from "../../lib/formatDateTime";
import { usePageTitle } from "../../lib/usePageTitle";
import { pageTitleClassName } from "../../components/PageTitle";
import PageContainer from "../../components/PageContainer";
import PageHeader from "../../components/PageHeader";
import { cardBorderClassName } from "../../lib/cardStyles";
import { normalizePhoneDigits } from "../../lib/callersData";

const API_BASE =
  import.meta.env.VITE_API_URL || "https://app.neurospherevoiceai.com/api";

type MemoryGroupId = "facts" | "interpreted" | "episodic" | "inferences";

type NormalizedMemory = {
  id: string;
  userId: string;
  key: string;
  type: string;
  value: unknown;
  createdAt?: string;
  group: MemoryGroupId;
};

type Caller = {
  phone: string;
  name: string | null;
  memories: NormalizedMemory[];
};

const MEMORY_GROUPS: { id: MemoryGroupId; label: string }[] = [
  { id: "facts", label: "Facts" },
  { id: "interpreted", label: "Interpreted" },
  { id: "episodic", label: "Episodic" },
  { id: "inferences", label: "Inferences" },
];

function getCustomerId(): string | null {
  return (
    localStorage.getItem("customerId") || sessionStorage.getItem("customerId")
  );
}

function getRecentCallers(): { phone: string; name: string | null }[] {
  try {
    const parsed: unknown = JSON.parse(localStorage.getItem("ns_recent_callers") || "[]");
    if (!Array.isArray(parsed)) return [];
    return parsed.filter(
      (r): r is { phone: string; name: string | null } =>
        typeof r === "object" && r !== null && typeof r.phone === "string" && r.phone.trim() !== "",
    );
  } catch {
    return [];
  }
}

function saveRecentCaller(phone: string, name: string | null) {
  const next = [
    { phone, name },
    ...getRecentCallers().filter((r) => r.phone !== phone),
  ].slice(0, 5);
  localStorage.setItem("ns_recent_callers", JSON.stringify(next));
}

function formatPhoneDisplay(phone: string): string {
  const d = normalizePhoneDigits(phone);
  if (d.length === 10) {
    return `+1 (${d.slice(0, 3)}) ${d.slice(3, 6)}-${d.slice(6)}`;
  }
  return phone;
}

function formatDate(dateStr: string | null | undefined): string {
  if (!dateStr) return "—";
  try {
    const date = parseDateTime(dateStr);
    if (!date) return "—";
    const now = new Date();
    const diffMs = now.getTime() - date.getTime();
    const diffDays = Math.floor(diffMs / (1000 * 60 * 60 * 24));
    if (diffDays === 0) return "Today";
    if (diffDays === 1) return "Yesterday";
    if (diffDays < 7) return `${diffDays} days ago`;
    return date.toLocaleDateString("en-US", { month: "long", day: "numeric", year: "numeric" });
  } catch {
    return "—";
  }
}

function classifyMemoryGroup(type: string, raw?: Record<string, unknown>): MemoryGroupId {
  const t = (type || "").toLowerCase().trim();
  const category = String(raw?.category ?? raw?.memory_category ?? raw?.group ?? "")
    .toLowerCase()
    .trim();

  if (category === "facts" || category === "fact") return "facts";
  if (category === "interpreted") return "interpreted";
  if (category === "episodic") return "episodic";
  if (category === "inferences" || category === "inference") return "inferences";

  if (t === "fact" || t === "facts") return "facts";
  if (
    t === "interpreted" ||
    t === "person" ||
    t === "preference" ||
    t === "project" ||
    t === "identity"
  ) {
    return "interpreted";
  }
  if (t === "episodic" || t === "moment" || t === "call_summary" || t === "summary") {
    return "episodic";
  }
  if (t === "inference" || t === "inferences" || t === "rule") return "inferences";

  return "facts";
}

function formatMemoryValue(value: unknown): string {
  if (value == null) return "—";
  if (typeof value === "string") {
    try {
      const parsed = JSON.parse(value);
      if (parsed !== null && typeof parsed === "object" && !Array.isArray(parsed)) {
        const o = parsed as Record<string, unknown>;
        if (typeof o.text === "string") return o.text;
        if (typeof o.name === "string") return o.name;
        if (typeof o.value === "number") return String(o.value);
        if (o.value != null && !isNaN(Number(o.value))) return String(Number(o.value));
        if (typeof o.value === "string") return o.value;
        if (o.value !== undefined && o.value !== null) return String(o.value);
      }
    } catch { /* not JSON — return as-is */ }
    return value;
  }
  if (typeof value === "number" || typeof value === "boolean") return String(value);
  if (typeof value === "object") {
    const o = value as Record<string, unknown>;
    if (typeof o.text === "string") return o.text;
    if (typeof o.name === "string") return o.name;
    if (typeof o.value === "number") return String(o.value);
    if (o.value != null && !isNaN(Number(o.value))) return String(Number(o.value));
    if (typeof o.value === "string") return o.value;
    if (typeof o.description === "string") return o.description;
    if (o.value !== undefined && o.value !== null) return String(o.value);
    try {
      return JSON.stringify(value, null, 2);
    } catch {
      return String(value);
    }
  }
  return String(value);
}

function extractCallerName(memories: NormalizedMemory[]): string | null {
  for (const m of memories) {
    const k = m.key.toLowerCase();
    if (k === "preferred_name" || k === "caller_name" || k === "name") {
      const v = formatMemoryValue(m.value);
      if (v && v !== "—") return v;
    }
    if (m.type === "person" || m.type === "identity") {
      if (typeof m.value === "object" && m.value !== null) {
        const name = (m.value as Record<string, unknown>).name;
        if (typeof name === "string" && name.trim()) return name.trim();
      }
    }
  }
  return null;
}

function normalizeApiMemory(raw: Record<string, unknown>): NormalizedMemory | null {
  const userId = String(raw.user_id ?? raw.userId ?? "").trim();
  if (!userId) return null;

  const key = String(raw.key ?? raw.k ?? "").trim();
  const type = String(raw.type ?? raw.memory_type ?? raw.memoryType ?? "fact").trim();
  const id = String(raw.id ?? raw.memory_id ?? raw.memoryId ?? `${userId}:${key}:${type}`);

  return {
    id,
    userId,
    key: key || "(no key)",
    type: type || "fact",
    value: raw.value ?? raw.value_json ?? raw.data,
    createdAt: String(
      raw.created_at ?? raw.timestamp ?? raw.createdAt ?? ""
    ).trim() || undefined,
    group: classifyMemoryGroup(type, raw),
  };
}

function parseMemoriesResponse(data: unknown): NormalizedMemory[] {
  if (!data || typeof data !== "object") return [];
  const obj = data as Record<string, unknown>;
  const list = obj.memories ?? obj.data ?? obj.results;
  if (!Array.isArray(list)) return [];
  return list
    .map((item) =>
      typeof item === "object" && item !== null
        ? normalizeApiMemory(item as Record<string, unknown>)
        : null
    )
    .filter((m): m is NormalizedMemory => m !== null);
}

function pushMemoryItems(
  memories: NormalizedMemory[],
  items: unknown,
  userId: string,
  defaultType: string,
  group: MemoryGroupId
) {
  if (!Array.isArray(items)) return;
  for (const item of items) {
    if (typeof item !== "object" || item === null) continue;
    const raw = item as Record<string, unknown>;
    const normalized = normalizeApiMemory({
      ...raw,
      user_id: userId,
      type: raw.type ?? raw.memory_type ?? defaultType,
    });
    if (normalized) {
      memories.push({ ...normalized, group });
    }
  }
}

function parseUserMemoriesResponse(data: unknown, phone: string): NormalizedMemory[] {
  const fromFlat = parseMemoriesResponse(data);
  if (fromFlat.length > 0) return fromFlat;

  if (!data || typeof data !== "object") return [];
  const obj = data as Record<string, unknown>;
  const userId = normalizePhoneDigits(phone) || phone;
  const memories: NormalizedMemory[] = [];

  pushMemoryItems(memories, obj.facts, userId, "fact", "facts");
  pushMemoryItems(memories, obj.preferences, userId, "preference", "interpreted");
  pushMemoryItems(memories, obj.relationships, userId, "person", "interpreted");
  pushMemoryItems(memories, obj.personality_insights, userId, "inference", "inferences");

  const summaries = obj.call_summaries;
  if (Array.isArray(summaries)) {
    for (const item of summaries) {
      if (typeof item !== "object" || item === null) continue;
      const raw = item as Record<string, unknown>;
      memories.push({
        id: String(raw.id ?? raw.call_sid ?? `summary:${memories.length}`),
        userId,
        key: String(raw.key ?? "call_summary"),
        type: "call_summary",
        value: raw.summary ?? raw.summary_text ?? raw.value,
        createdAt:
          String(raw.call_date ?? raw.created_at ?? raw.timestamp ?? "").trim() ||
          undefined,
        group: "episodic",
      });
    }
  }

  return memories.sort((a, b) => {
    const ta = a.createdAt ?? "";
    const tb = b.createdAt ?? "";
    return tb.localeCompare(ta);
  });
}

function buildCallerFromResponse(phone: string, data: unknown): Caller {
  const memories = parseUserMemoriesResponse(data, phone);
  const obj =
    data && typeof data === "object" ? (data as Record<string, unknown>) : null;
  const identity =
    obj?.identity && typeof obj.identity === "object"
      ? (obj.identity as Record<string, unknown>)
      : null;
  const nameFromIdentity =
    identity && typeof identity.preferred_name === "string"
      ? identity.preferred_name.trim() || null
      : null;

  return {
    phone,
    name: nameFromIdentity || extractCallerName(memories),
    memories,
  };
}

function getFactValue(memories: NormalizedMemory[], ...keys: string[]): string {
  for (const key of keys) {
    const found = memories.find((m) => m.key.toLowerCase() === key.toLowerCase());
    if (found) {
      const v = formatMemoryValue(found.value);
      if (v && v !== "—") return v;
    }
  }
  return "—";
}

function parseValueForApi(text: string): Record<string, unknown> {
  const trimmed = text.trim();
  if (!trimmed) return {};
  try {
    const parsed = JSON.parse(trimmed);
    if (parsed !== null && typeof parsed === "object" && !Array.isArray(parsed)) {
      return parsed as Record<string, unknown>;
    }
    return { value: parsed };
  } catch {
    return { text: trimmed };
  }
}

type CallSummary = {
  call_id: string;
  call_date: string;
  summary: string;
  key_topics: string[];
  sentiment: string;
  resolution_status: string;
  user_id: string;
};

function extractCallSummaries(data: unknown): CallSummary[] {
  if (!data || typeof data !== "object") return [];
  const obj = data as Record<string, unknown>;
  if (!Array.isArray(obj.call_summaries)) return [];
  return obj.call_summaries.map((item) => {
    const raw = (typeof item === "object" && item !== null ? item : {}) as Record<string, unknown>;
    return {
      call_id: String(raw.call_id ?? raw.id ?? raw.call_sid ?? ""),
      call_date: String(raw.call_date ?? raw.created_at ?? raw.timestamp ?? ""),
      summary: String(raw.summary ?? raw.summary_text ?? raw.value ?? ""),
      key_topics: Array.isArray(raw.key_topics) ? raw.key_topics.map((t) => String(t)) : [],
      sentiment: String(raw.sentiment ?? ""),
      resolution_status: String(raw.resolution_status ?? ""),
      user_id: String(raw.user_id ?? raw.userId ?? ""),
    };
  });
}

function MemoryRow({ memory }: { memory: NormalizedMemory }) {
  const display = formatMemoryValue(memory.value);
  const isJson =
    typeof memory.value === "object" &&
    memory.value !== null &&
    display.includes("\n");

  return (
    <div
      className={`p-3 rounded-lg ${cardBorderClassName}`}
      style={{
        background: "rgba(13, 13, 18, 0.6)",
      }}
    >
      <div className="flex items-start justify-between gap-3 mb-1">
        <span
          className="text-purple-300 font-semibold text-sm"
          style={{ fontFamily: "'Space Grotesk', sans-serif" }}
        >
          {memory.key}
        </span>
        <span className="text-xs text-gray-500 uppercase shrink-0">{memory.type}</span>
      </div>
      {isJson ? (
        <pre className="text-xs text-gray-300 overflow-x-auto whitespace-pre-wrap mb-0">
          {display}
        </pre>
      ) : (
        <p
          className="text-gray-300 text-sm mb-0"
          style={{ fontFamily: "'Space Grotesk', sans-serif" }}
        >
          {display}
        </p>
      )}
      {memory.createdAt && (
        <div className="flex items-center gap-2 mt-2 text-xs text-gray-500">
          <i className="ri-time-line" />
          <span>{formatDate(memory.createdAt)}</span>
        </div>
      )}
    </div>
  );
}

export default function UserMemories() {
  usePageTitle("Callers");
  const [phoneInput, setPhoneInput] = useState("");
  const [activePhone, setActivePhone] = useState<string | null>(null);
  const [caller, setCaller] = useState<Caller | null>(null);
  const [hasSearched, setHasSearched] = useState(false);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [validationHint, setValidationHint] = useState<string | null>(null);
  const [statusMessage, setStatusMessage] = useState<string | null>(null);

  const [addMemoryPhone, setAddMemoryPhone] = useState<string | null>(null);
  const [newMemoryKey, setNewMemoryKey] = useState("");
  const [newMemoryValue, setNewMemoryValue] = useState("");
  const [savingMemory, setSavingMemory] = useState(false);

  const [contactEditMode, setContactEditMode] = useState(false);
  const [contactEdits, setContactEdits] = useState({
    full_name: "",
    email: "",
    phone: "",
    business_name: "",
    address: "",
    notes: "",
  });
  const [showAddSummary, setShowAddSummary] = useState(false);
  const [summaryDate, setSummaryDate] = useState("");
  const [summaryUserId, setSummaryUserId] = useState("");
  const [summaryText, setSummaryText] = useState("");
  const [savingSummary, setSavingSummary] = useState(false);
  const [callSummaries, setCallSummaries] = useState<CallSummary[]>([]);

  const [callersList, setCallersList] = useState<CallerRow[] | null>(null);
  const [callersTotal, setCallersTotal] = useState(0);
  const [callersLoading, setCallersLoading] = useState(false);
  const [callersError, setCallersError] = useState(false);
  const callersRequested = useRef(false);
  const callersLoadId = useRef(0);

  const loadCallers = useCallback(async () => {
    const loadId = ++callersLoadId.current;
    setCallersLoading(true);
    setCallersError(false);
    const customerId = getCustomerId();
    if (!customerId) {
      setCallersError(true);
      setCallersLoading(false);
      return;
    }
    let rows: CallerRow[];
    try {
      const page = await fetchCallers(customerId);
      if (loadId !== callersLoadId.current) return;
      rows = page.callers;
      setCallersList(rows);
      setCallersTotal(page.total);
    } catch {
      if (loadId === callersLoadId.current) setCallersError(true);
      return;
    } finally {
      if (loadId === callersLoadId.current) setCallersLoading(false);
    }
    const names = await hydrateNames(customerId, rows);
    if (loadId !== callersLoadId.current || names.size === 0) return;
    setCallersList((prev) =>
      prev ? prev.map((r) => (r.name === null && names.has(r.phone) ? { ...r, name: names.get(r.phone)! } : r)) : prev,
    );
  }, []);

  useEffect(() => {
    if (hasSearched || callersRequested.current) return;
    if (new URLSearchParams(window.location.search).get("phone")) return;
    callersRequested.current = true;
    void loadCallers();
  }, [hasSearched, loadCallers]);

  const searchMemories = useCallback(async (phone: string) => {
    setValidationHint(null);
    const trimmed = phone.trim();
    if (!trimmed) {
      setError("Enter a phone number to search.");
      return;
    }

    const customerId = getCustomerId();
    if (!customerId) {
      setError("No customer ID found. Please log in again.");
      setCaller(null);
      return;
    }

    setLoading(true);
    setError(null);
    setHasSearched(true);
    setActivePhone(trimmed);
    setAddMemoryPhone(null);

    try {
      const res = await fetch(
        `${API_BASE}/customers/${customerId}/user-memories?phone=${encodeURIComponent(trimmed)}`,
        { method: "GET", credentials: "include" }
      );

      if (res.status === 401 || res.status === 403) {
        setError("Unauthorized — please log in again.");
        setCaller(null);
        return;
      }

      if (!res.ok) {
        if (res.status === 400) {
          setError("That doesn't look like a phone number. Enter a 10-digit number with area code.");
        } else {
          const errText = await res.text().catch(() => "");
          setError(
            `Failed to load memories (HTTP ${res.status})${errText ? `: ${errText.slice(0, 120)}` : ""}`
          );
        }
        setCaller(null);
        return;
      }

      const data = await res.json();
      if (data?.success === false) {
        setError(data?.error || data?.message || "Failed to load memories.");
        setCaller(null);
        return;
      }

      if (data?.error && !data?.metadata) {
        setError(String(data.error));
        setCaller(null);
        return;
      }

      const built = buildCallerFromResponse(trimmed, data);
      setCaller(built);
      saveRecentCaller(trimmed, built.name);
      setCallSummaries(extractCallSummaries(data));
    } catch (e: unknown) {
      const msg = e instanceof Error ? e.message : String(e);
      setError(msg || "Network error while loading memories.");
      setCaller(null);
    } finally {
      setLoading(false);
    }
  }, []);

  const validateAndSearch = (phone: string) => {
    if (phone.replace(/\D/g, "").length < 10) {
      setValidationHint("Enter a phone number with area code, for example 949 555 1234. Name search is coming soon.");
      return;
    }
    searchMemories(phone);
  };

  const handleSearchSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    validateAndSearch(phoneInput);
  };

  const flashStatus = (msg: string) => {
    setStatusMessage(msg);
    window.setTimeout(() => setStatusMessage(null), 3500);
  };

  const resetToHome = () => {
    setHasSearched(false);
    setCaller(null);
    setPhoneInput("");
    setError(null);
    setValidationHint(null);
    setActivePhone(null);
    setCallSummaries([]);
    window.history.replaceState({}, "", "/user-memories");
  };

  const submitNewMemory = async (phone: string) => {
    const customerId = getCustomerId();
    if (!customerId) {
      setError("No customer ID found.");
      return;
    }

    const key = newMemoryKey.trim();
    const valueText = newMemoryValue.trim();
    if (!key || !valueText) {
      flashStatus("Key and value are required.");
      return;
    }

    setSavingMemory(true);
    try {
      const user_id = normalizePhoneDigits(phone) || phone;
      const res = await fetch(
        `${API_BASE}/customers/${customerId}/memories`,
        {
          method: "POST",
          credentials: "include",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify({
            user_id,
            type: "fact",
            key,
            value: parseValueForApi(valueText),
          }),
        }
      );

      const data = await res.json().catch(() => ({}));
      if (!res.ok) {
        const det =
          data?.error ||
          data?.message ||
          (Array.isArray(data?.details) ? data.details.join("; ") : "");
        flashStatus(`Save failed: HTTP ${res.status}${det ? ` — ${det}` : ""}`);
        return;
      }

      setAddMemoryPhone(null);
      setNewMemoryKey("");
      setNewMemoryValue("");
      flashStatus("Memory saved.");
      await searchMemories(phone);
      setAddMemoryPhone(phone);
    } catch (e: unknown) {
      flashStatus(e instanceof Error ? e.message : "Failed to save memory.");
    } finally {
      setSavingMemory(false);
    }
  };

  const submitMemoryDirect = async (
    phone: string,
    key: string,
    valueText: string
  ): Promise<boolean> => {
    const customerId = getCustomerId();
    if (!customerId) { setError("No customer ID found."); return false; }
    if (!key.trim() || !valueText.trim()) return false;
    try {
      const user_id = normalizePhoneDigits(phone) || phone;
      const res = await fetch(`${API_BASE}/customers/${customerId}/memories`, {
        method: "POST",
        credentials: "include",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          user_id,
          type: "fact",
          key: key.trim(),
          value: parseValueForApi(valueText.trim()),
        }),
      });
      const data = await res.json().catch(() => ({}));
      if (!res.ok) {
        const det =
          data?.error ||
          data?.message ||
          (Array.isArray(data?.details) ? data.details.join("; ") : "");
        flashStatus(`Save failed: HTTP ${res.status}${det ? ` — ${det}` : ""}`);
        return false;
      }
      return true;
    } catch (e: unknown) {
      flashStatus(e instanceof Error ? e.message : "Failed to save.");
      return false;
    }
  };

  const handleSaveContact = async (phone: string) => {
    const fields = [
      { key: "full_name", value: contactEdits.full_name },
      { key: "email", value: contactEdits.email },
      { key: "phone", value: contactEdits.phone },
      { key: "business_name", value: contactEdits.business_name },
      { key: "address", value: contactEdits.address },
      { key: "notes", value: contactEdits.notes },
    ].filter((f) => f.value.trim());

    let anyFailed = false;
    for (const f of fields) {
      const ok = await submitMemoryDirect(phone, f.key, f.value);
      if (!ok) anyFailed = true;
    }
    if (!anyFailed) {
      setContactEditMode(false);
      flashStatus("Contact info saved.");
      await searchMemories(phone);
    }
  };

  const handleSaveSummary = async (phone: string) => {
    if (!summaryText.trim()) { flashStatus("Summary text is required."); return; }
    setSavingSummary(true);
    const ok = await submitMemoryDirect(
      phone,
      "call_summary",
      JSON.stringify({ date: summaryDate, userId: summaryUserId, summary: summaryText })
    );
    setSavingSummary(false);
    if (ok) {
      setShowAddSummary(false);
      setSummaryDate("");
      setSummaryUserId("");
      setSummaryText("");
      flashStatus("Summary saved.");
      await searchMemories(phone);
    }
  };

  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    const phoneParam = params.get("phone");
    if (phoneParam) {
      setPhoneInput(phoneParam);
      searchMemories(phoneParam);
    }
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  const memoryCount = caller?.memories.length ?? 0;
  const recentLookups = hasSearched ? [] : getRecentCallers().slice(0, 5);

  return (
    <CoreLayout>
      <PageContainer>
        <PageHeader
          rawTitle={hasSearched}
          title={
            hasSearched ? (
              <button
                type="button"
                className={`${pageTitleClassName} cursor-pointer`}
                title="Back to callers list"
                onClick={resetToHome}
              >
                Callers
              </button>
            ) : (
              "Callers"
            )
          }
          subtitle="Every caller, their memories, and context in one place"
        />

        <form className="mb-6" onSubmit={handleSearchSubmit}>
          <div className="relative flex gap-3">
            <div className="relative flex-1">
              <input
                type="text"
                value={phoneInput}
                onChange={(e) => { setPhoneInput(e.target.value); setValidationHint(null); if (!e.target.value) setError(null); }}
                placeholder="Search by phone number"
                className="w-full px-6 py-4 pl-14 bg-[#1a1a24] border-2 rounded-xl text-white text-lg focus:outline-none transition-all duration-300"
                style={{
                  borderColor: phoneInput
                    ? "#ff6a00"
                    : "rgba(138, 43, 226, 0.4)",
                  boxShadow: phoneInput
                    ? "0 0 20px rgba(255, 106, 0, 0.3)"
                    : "0 0 15px rgba(138, 43, 226, 0.2)",
                  fontFamily: "'Space Grotesk', sans-serif",
                }}
              />
              <i className="ri-phone-line absolute left-5 top-1/2 -translate-y-1/2 text-2xl text-purple-400" />
              {phoneInput && (
                <button
                  type="button"
                  onClick={() => { setPhoneInput(""); setValidationHint(null); }}
                  className="absolute right-5 top-1/2 -translate-y-1/2 w-8 h-8 flex items-center justify-center rounded-full bg-purple-600/20 hover:bg-purple-600/40 transition-colors cursor-pointer"
                >
                  <i className="ri-close-line text-purple-400" />
                </button>
              )}
            </div>
            <button
              type="submit"
              disabled={loading}
              className="px-6 py-4 rounded-xl text-white font-semibold shrink-0 cursor-pointer disabled:opacity-50"
              style={{
                background: "linear-gradient(90deg, #8a2be2, #ff6a00)",
                fontFamily: "'Space Grotesk', sans-serif",
              }}
            >
              {loading ? "Searching…" : "Search"}
            </button>
          </div>
        </form>

        {statusMessage && (
          <div
            className="mb-4 px-4 py-3 rounded-lg text-sm text-center"
            style={{
              background: "rgba(138, 43, 226, 0.15)",
              border: "1px solid rgba(138, 43, 226, 0.35)",
              color: "rgba(255,255,255,0.9)",
              fontFamily: "'Space Grotesk', sans-serif",
            }}
          >
            {statusMessage}
          </div>
        )}

        {error && (
          <div
            className="mb-6 p-4 rounded-xl text-center"
            style={{
              background: "rgba(220, 38, 38, 0.12)",
              border: "1px solid rgba(248, 113, 113, 0.35)",
            }}
          >
            <p className="text-red-300 text-sm" style={{ fontFamily: "'Space Grotesk', sans-serif" }}>
              {error}
            </p>
            <button
              type="button"
              onClick={() => validateAndSearch(activePhone || phoneInput)}
              className="mt-3 px-4 py-2 rounded-lg text-sm font-semibold text-white cursor-pointer"
              style={{
                background: "linear-gradient(90deg, #8a2be2, #ff6a00)",
              }}
            >
              Retry
            </button>
          </div>
        )}

        {validationHint && (
          <p className="text-sm text-orange-300 text-center mb-6">{validationHint}</p>
        )}

        <div className="space-y-4">
          {loading ? (
            <div className="text-center py-16">
              <div
                className="inline-block w-10 h-10 border-2 border-purple-500 border-t-transparent rounded-full animate-spin mb-4"
                style={{ borderTopColor: "transparent" }}
              />
              <p
                className="text-gray-400"
                style={{ fontFamily: "'Space Grotesk', sans-serif" }}
              >
                Loading memories…
              </p>
            </div>
          ) : !hasSearched && (callersLoading || (callersList === null && !callersError)) ? (
            <div className="text-center py-16">
              <div
                className="inline-block w-10 h-10 border-2 border-purple-500 border-t-transparent rounded-full animate-spin mb-4"
                style={{ borderTopColor: "transparent" }}
              />
              <p
                className="text-gray-400"
                style={{ fontFamily: "'Space Grotesk', sans-serif" }}
              >
                Loading callers…
              </p>
            </div>
          ) : !hasSearched && callersError ? (
            <div
              className="p-4 rounded-xl text-center"
              style={{
                background: "rgba(220, 38, 38, 0.12)",
                border: "1px solid rgba(248, 113, 113, 0.35)",
              }}
            >
              <p className="text-red-300 text-sm" style={{ fontFamily: "'Space Grotesk', sans-serif" }}>
                Couldn't load callers
              </p>
              <button
                type="button"
                onClick={() => void loadCallers()}
                className="mt-3 px-4 py-2 rounded-lg text-sm font-semibold text-white cursor-pointer"
                style={{
                  background: "linear-gradient(90deg, #8a2be2, #ff6a00)",
                }}
              >
                Retry
              </button>
            </div>
          ) : !hasSearched && callersList && callersList.length > 0 ? (
            <div>
              <div className="flex items-baseline justify-between gap-3 mb-3">
                <h3
                  className="text-white font-semibold"
                  style={{ fontFamily: "'Space Grotesk', sans-serif" }}
                >
                  All callers
                </h3>
                <span
                  className="text-xs text-gray-500"
                  style={{ fontFamily: "'Space Grotesk', sans-serif" }}
                >
                  {callersTotal} {callersTotal === 1 ? "caller" : "callers"}
                </span>
              </div>
              <div className="space-y-2">
                {callersList.map((row) => (
                  <div
                    key={row.phone}
                    className={`group flex items-center gap-3 p-4 rounded-lg hover:bg-white/5 ${cardBorderClassName}`}
                    style={{
                      background: "rgba(13, 13, 18, 0.6)",
                    }}
                  >
                    <button
                      type="button"
                      onClick={() => {
                        setPhoneInput(row.phone);
                        searchMemories(row.phone);
                      }}
                      className="flex-1 min-w-0 text-left cursor-pointer rounded-md focus:outline-none focus-visible:ring-2 focus-visible:ring-[#8a2be2]"
                    >
                      <div className="flex flex-wrap items-center gap-3 mb-1">
                        <span
                          className={`text-sm font-semibold text-white ${row.name ? "" : "font-mono"}`}
                          style={row.name ? { fontFamily: "'Space Grotesk', sans-serif" } : undefined}
                        >
                          {row.name || formatPhoneDisplay(row.phone)}
                        </span>
                        <i
                          className="ri-arrow-right-s-line text-gray-600 group-hover:text-gray-300 ml-auto"
                          aria-hidden
                        />
                      </div>
                      <div className="flex flex-wrap items-center gap-2">
                        <span
                          className="text-xs text-gray-500"
                          style={{ fontFamily: "'Space Grotesk', sans-serif" }}
                        >
                          {row.call_count} {row.call_count === 1 ? "call" : "calls"} · last{" "}
                          {formatDateTime(row.last_call_at)}
                        </span>
                        {row.open_items_count > 0 && (
                          <span
                            className="px-2 py-0.5 rounded-full text-xs font-semibold"
                            style={{
                              background: "rgba(255,106,0,0.15)",
                              border: "1px solid rgba(255,106,0,0.35)",
                              color: "rgb(253,186,116)",
                              fontFamily: "'Space Grotesk', sans-serif",
                            }}
                          >
                            {row.open_items_count} open
                          </span>
                        )}
                      </div>
                    </button>
                  </div>
                ))}
              </div>
            </div>
          ) : !hasSearched ? (
            <div className="text-center py-16 max-w-md mx-auto">
              <i className="ri-user-search-line text-5xl text-purple-400/30 mb-5 block" />
              <h3
                className="text-lg font-semibold text-white mb-3"
                style={{ fontFamily: "'Space Grotesk', sans-serif" }}
              >
                Look up a caller
              </h3>
              <p className="text-sm text-gray-400">
                Enter a phone number to see everything your receptionist remembers about that caller: name, past calls, preferences and open follow-ups.
              </p>
              {recentLookups.length > 0 && (
                <div className="mt-8 text-left">
                  <p className="text-xs uppercase tracking-wide text-gray-500 mb-2">
                    Recent lookups
                  </p>
                  {recentLookups.map((recent) => (
                    <button
                      key={recent.phone}
                      type="button"
                      onClick={() => {
                        setPhoneInput(recent.phone);
                        searchMemories(recent.phone);
                      }}
                      className="block w-full text-left px-3 py-2 rounded-md text-sm text-gray-300 hover:bg-white/5 font-mono cursor-pointer"
                    >
                      {formatPhoneDisplay(recent.phone)}
                    </button>
                  ))}
                </div>
              )}
            </div>
          ) : !caller ? (
            <div className="text-center py-16">
              <i className="ri-user-search-line text-6xl text-purple-400/30 mb-5 block" />
              <p
                className="text-gray-400"
                style={{ fontFamily: "'Space Grotesk', sans-serif" }}
              >
                No memory profile found for this number.
              </p>
            </div>
          ) : (
            <div className="space-y-6">

              {/* ── Section 1: Profile Header ─────────────────────────────── */}
              <div
                className={`rounded-xl backdrop-blur-sm overflow-hidden ${cardBorderClassName}`}
                style={{
                  background: "rgba(28, 28, 37, 0.8)",
                }}
              >
                <div className="p-5 flex items-center gap-4">
                  <div
                    className="shrink-0 w-14 h-14 rounded-full flex items-center justify-center text-2xl font-bold text-white"
                    style={{ background: "linear-gradient(135deg, #8a2be2, #ff6a00)" }}
                  >
                    {caller.name ? caller.name.charAt(0).toUpperCase() : "?"}
                  </div>
                  <div className="flex-1 min-w-0">
                    <div className="flex flex-wrap items-center gap-3 mb-1">
                      <h2
                        className="text-xl font-semibold text-white"
                        style={{ fontFamily: "'Space Grotesk', sans-serif" }}
                      >
                        {caller.name ?? "Name unknown"}
                      </h2>
                      <span
                        className="text-xs px-3 py-1 rounded-full shrink-0"
                        style={{
                          background: "rgba(138, 43, 226, 0.2)",
                          color: "rgba(255,255,255,0.75)",
                          fontFamily: "'Space Grotesk', sans-serif",
                        }}
                      >
                        {memoryCount} {memoryCount === 1 ? "memory" : "memories"}
                      </span>
                    </div>
                    <div className="flex items-center gap-2">
                      <i className="ri-phone-line text-purple-400 text-sm" />
                      <span className="text-orange-400 font-mono text-sm">
                        {formatPhoneDisplay(caller.phone)}
                      </span>
                    </div>
                    <div
                      className="text-xs text-gray-500 mt-1"
                      style={{ fontFamily: "'Space Grotesk', sans-serif" }}
                    >
                      Last seen:{" "}
                      {(() => {
                        const dates = caller.memories
                          .map((m) => m.createdAt)
                          .filter(Boolean) as string[];
                        return dates.length > 0
                          ? formatDate([...dates].sort().at(-1)!)
                          : "Never";
                      })()}
                    </div>
                  </div>
                </div>
              </div>

              {/* ── Section 2: Contact Info ───────────────────────────────── */}
              <div
                className={`rounded-xl backdrop-blur-sm overflow-hidden ${cardBorderClassName}`}
                style={{
                  background: "rgba(28, 28, 37, 0.8)",
                }}
              >
                <div className="p-5">
                  <div className="flex items-center justify-between mb-4">
                    <h3
                      className="text-white font-semibold"
                      style={{ fontFamily: "'Space Grotesk', sans-serif" }}
                    >
                      Contact Info
                    </h3>
                    {!contactEditMode && (
                      <button
                        type="button"
                        onClick={() => {
                          setContactEdits({
                            full_name: getFactValue(caller.memories, "full_name", "name", "preferred_name", "caller_name").replace(/^—$/, ""),
                            email: getFactValue(caller.memories, "email").replace(/^—$/, ""),
                            phone: getFactValue(caller.memories, "phone").replace(/^—$/, ""),
                            business_name: getFactValue(caller.memories, "business_name", "business").replace(/^—$/, ""),
                            address: getFactValue(caller.memories, "address", "business_address").replace(/^—$/, ""),
                            notes: getFactValue(caller.memories, "notes").replace(/^—$/, ""),
                          });
                          setContactEditMode(true);
                        }}
                        className="p-2 rounded-lg cursor-pointer"
                        style={{
                          background: "rgba(138, 43, 226, 0.15)",
                          border: "1px solid rgba(138, 43, 226, 0.3)",
                        }}
                      >
                        <i className="ri-pencil-line text-purple-400 text-sm" />
                      </button>
                    )}
                  </div>

                  {contactEditMode ? (
                    <div className="space-y-3">
                      {(
                        [
                          { key: "full_name" as const, label: "Full Name" },
                          { key: "email" as const, label: "Email" },
                          { key: "phone" as const, label: "Phone" },
                          { key: "business_name" as const, label: "Business" },
                          { key: "address" as const, label: "Address" },
                          { key: "notes" as const, label: "Notes" },
                        ]
                      ).map(({ key, label }) => (
                        <div key={key}>
                          <label
                            className="block text-xs text-gray-400 mb-1"
                            style={{ fontFamily: "'Space Grotesk', sans-serif" }}
                          >
                            {label}
                          </label>
                          <input
                            type="text"
                            value={contactEdits[key]}
                            onChange={(e) =>
                              setContactEdits((prev) => ({ ...prev, [key]: e.target.value }))
                            }
                            className="w-full px-3 py-2 rounded-lg bg-black/40 border border-purple-500/30 text-white text-sm focus:outline-none focus:border-purple-500"
                            style={{ fontFamily: "'Space Grotesk', sans-serif" }}
                          />
                        </div>
                      ))}
                      <div className="flex gap-2 pt-1">
                        <button
                          type="button"
                          onClick={() => handleSaveContact(caller.phone)}
                          className="px-4 py-2 rounded-lg text-sm font-semibold text-white cursor-pointer"
                          style={{ background: "linear-gradient(90deg, #8a2be2, #ff6a00)" }}
                        >
                          Save Changes
                        </button>
                        <button
                          type="button"
                          onClick={() => setContactEditMode(false)}
                          className="px-4 py-2 rounded-lg text-sm text-gray-300 cursor-pointer"
                          style={{
                            background: "rgba(255,255,255,0.06)",
                            border: "1px solid rgba(138, 43, 226, 0.2)",
                          }}
                        >
                          Cancel
                        </button>
                      </div>
                    </div>
                  ) : (
                    <div className="space-y-3">
                      {[
                        { label: "Full Name", value: getFactValue(caller.memories, "full_name", "name", "preferred_name", "caller_name") },
                        { label: "Email", value: getFactValue(caller.memories, "email") },
                        { label: "Phone", value: getFactValue(caller.memories, "phone") },
                        { label: "Business", value: getFactValue(caller.memories, "business_name", "business") },
                        { label: "Address", value: getFactValue(caller.memories, "address", "business_address") },
                        { label: "Notes", value: getFactValue(caller.memories, "notes") },
                      ].map(({ label, value }) => (
                        <div key={label} className="flex gap-4">
                          <span
                            className="text-xs text-gray-500 w-28 shrink-0 pt-0.5"
                            style={{ fontFamily: "'Space Grotesk', sans-serif" }}
                          >
                            {label}
                          </span>
                          <span
                            className="text-sm text-white/80 flex-1"
                            style={{ fontFamily: "'Space Grotesk', sans-serif" }}
                          >
                            {value}
                          </span>
                        </div>
                      ))}
                    </div>
                  )}
                </div>
              </div>

              {/* ── Section 3: Stats ──────────────────────────────────────── */}
              {(() => {
                const dates = caller.memories
                  .map((m) => m.createdAt)
                  .filter(Boolean) as string[];
                const sorted = [...dates].sort();
                const firstSeen = sorted[0] ? formatDate(sorted[0]) : "—";
                const lastActivity = sorted.at(-1) ? formatDate(sorted.at(-1)!) : "—";
                const groupsWithData = MEMORY_GROUPS.filter((g) =>
                  caller.memories.some((m) => m.group === g.id)
                ).length;
                const stats = [
                  { label: "Total Memories", value: String(caller.memories.length), icon: "ri-brain-line" },
                  { label: "Memory Groups", value: String(groupsWithData), icon: "ri-stack-line" },
                  { label: "First Seen", value: firstSeen, icon: "ri-calendar-line" },
                  { label: "Last Activity", value: lastActivity, icon: "ri-time-line" },
                ];
                return (
                  <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
                    {stats.map((s) => (
                      <div
                        key={s.label}
                        className={`rounded-xl p-4 ${cardBorderClassName}`}
                        style={{
                          background: "rgba(28, 28, 37, 0.8)",
                        }}
                      >
                        <div className="flex items-center gap-2 mb-2">
                          <i className={`${s.icon} text-purple-400 text-sm`} />
                          <span
                            className="text-xs text-gray-500 uppercase tracking-wide"
                            style={{ fontFamily: "'Space Grotesk', sans-serif" }}
                          >
                            {s.label}
                          </span>
                        </div>
                        <p
                          className="text-white font-semibold text-sm break-all"
                          style={{ fontFamily: "'Space Grotesk', sans-serif" }}
                        >
                          {s.value}
                        </p>
                      </div>
                    ))}
                  </div>
                );
              })()}

              {/* ── Section 4: Call Summaries ─────────────────────────────── */}
              <div
                className={`rounded-xl backdrop-blur-sm overflow-hidden ${cardBorderClassName}`}
                style={{
                  background: "rgba(28, 28, 37, 0.8)",
                }}
              >
                <div className="p-5">
                  <div className="flex items-center justify-between mb-4">
                    <h3
                      className="text-white font-semibold"
                      style={{ fontFamily: "'Space Grotesk', sans-serif" }}
                    >
                      Call Summaries
                    </h3>
                    <button
                      type="button"
                      onClick={() => {
                        setShowAddSummary((v) => !v);
                        setSummaryDate("");
                        setSummaryUserId("");
                        setSummaryText("");
                      }}
                      className="px-3 py-1.5 rounded-lg text-sm font-semibold text-white cursor-pointer"
                      style={{ background: "linear-gradient(90deg, #8a2be2, #ff6a00)" }}
                    >
                      + Add Summary
                    </button>
                  </div>

                  {showAddSummary && (
                    <div
                      className="mb-5 p-4 rounded-xl"
                      style={{
                        background: "rgba(26, 26, 36, 0.75)",
                        border: "1px solid rgba(138, 43, 226, 0.3)",
                      }}
                    >
                      <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 mb-3">
                        <div>
                          <label className="block text-xs text-gray-400 mb-1">Date / Time</label>
                          <input
                            type="text"
                            value={summaryDate}
                            onChange={(e) => setSummaryDate(e.target.value)}
                            placeholder="e.g. 2024-06-01 10:30am"
                            className="w-full px-3 py-2 rounded-lg bg-black/40 border border-purple-500/30 text-white text-sm focus:outline-none focus:border-purple-500"
                          />
                        </div>
                        <div>
                          <label className="block text-xs text-gray-400 mb-1">User ID</label>
                          <input
                            type="text"
                            value={summaryUserId}
                            onChange={(e) => setSummaryUserId(e.target.value)}
                            placeholder="Agent or caller ID"
                            className="w-full px-3 py-2 rounded-lg bg-black/40 border border-purple-500/30 text-white text-sm focus:outline-none focus:border-purple-500"
                          />
                        </div>
                      </div>
                      <div className="mb-3">
                        <label className="block text-xs text-gray-400 mb-1">Summary *</label>
                        <textarea
                          rows={3}
                          value={summaryText}
                          onChange={(e) => setSummaryText(e.target.value)}
                          placeholder="What happened during this call?"
                          className="w-full px-3 py-2 rounded-lg bg-black/40 border border-purple-500/30 text-white text-sm focus:outline-none focus:border-purple-500 resize-y"
                        />
                      </div>
                      <div className="flex gap-2">
                        <button
                          type="button"
                          disabled={savingSummary}
                          onClick={() => handleSaveSummary(caller.phone)}
                          className="px-4 py-2 rounded-lg text-sm font-semibold text-white cursor-pointer disabled:opacity-50"
                          style={{ background: "linear-gradient(90deg, #8a2be2, #ff6a00)" }}
                        >
                          {savingSummary ? "Saving…" : "Save Summary"}
                        </button>
                        <button
                          type="button"
                          onClick={() => setShowAddSummary(false)}
                          className="px-4 py-2 rounded-lg text-sm text-gray-300 cursor-pointer"
                          style={{
                            background: "rgba(255,255,255,0.06)",
                            border: "1px solid rgba(138, 43, 226, 0.2)",
                          }}
                        >
                          Cancel
                        </button>
                      </div>
                    </div>
                  )}

                  {callSummaries.length === 0 ? (
                    <div className="text-center py-8">
                      <i className="ri-file-list-3-line text-4xl text-purple-400/30 mb-3" />
                      <p
                        className="text-gray-400 text-sm"
                        style={{ fontFamily: "'Space Grotesk', sans-serif" }}
                      >
                        No call summaries yet. They will appear here automatically after calls.
                      </p>
                    </div>
                  ) : (
                    <div className="space-y-3">
                      {callSummaries.map((s) => {
                        const sentimentStyle = s.sentiment === "positive"
                          ? { background: "rgba(34,197,94,0.12)", border: "1px solid rgba(34,197,94,0.3)", color: "rgb(134,239,172)" }
                          : s.sentiment === "negative"
                          ? { background: "rgba(239,68,68,0.12)", border: "1px solid rgba(239,68,68,0.3)", color: "rgb(252,165,165)" }
                          : { background: "rgba(255,255,255,0.06)", border: "1px solid rgba(255,255,255,0.12)", color: "rgba(255,255,255,0.5)" };

                        const resolutionStyle = s.resolution_status === "resolved"
                          ? { background: "rgba(138,43,226,0.18)", border: "1px solid rgba(138,43,226,0.35)", color: "rgb(196,181,253)" }
                          : s.resolution_status === "escalated"
                          ? { background: "rgba(255,106,0,0.15)", border: "1px solid rgba(255,106,0,0.35)", color: "rgb(253,186,116)" }
                          : { background: "rgba(255,255,255,0.06)", border: "1px solid rgba(255,255,255,0.12)", color: "rgba(255,255,255,0.45)" };

                        return (
                          <div
                            key={s.call_id || s.call_date}
                            className="p-4 rounded-lg"
                            style={{
                              background: "rgba(13, 13, 18, 0.6)",
                              border: "1px solid rgba(138, 43, 226, 0.15)",
                            }}
                          >
                            {/* Row 1: date + sentiment badge */}
                            <div className="flex items-center justify-between gap-3 mb-2">
                              <span
                                className="flex items-center gap-1 text-xs text-gray-400"
                                style={{ fontFamily: "'Space Grotesk', sans-serif" }}
                              >
                                <i className="ri-time-line" />
                                {formatDateTime(s.call_date) || "—"}
                              </span>
                              {s.sentiment && (
                                <span
                                  className="px-2 py-0.5 rounded-full text-xs font-semibold capitalize"
                                  style={{ ...sentimentStyle, fontFamily: "'Space Grotesk', sans-serif" }}
                                >
                                  {s.sentiment}
                                </span>
                              )}
                            </div>

                            {/* Row 2: resolution status pill */}
                            {s.resolution_status && (
                              <div className="mb-2">
                                <span
                                  className="px-2 py-0.5 rounded-full text-xs font-semibold capitalize"
                                  style={{ ...resolutionStyle, fontFamily: "'Space Grotesk', sans-serif" }}
                                >
                                  {s.resolution_status}
                                </span>
                              </div>
                            )}

                            {/* Row 3: key topics chips */}
                            {s.key_topics.length > 0 && (
                              <div className="flex flex-wrap gap-1.5 mb-3">
                                {s.key_topics.map((topic) => (
                                  <span
                                    key={topic}
                                    className="px-2 py-0.5 rounded text-xs text-gray-400"
                                    style={{
                                      background: "rgba(255,255,255,0.06)",
                                      border: "1px solid rgba(255,255,255,0.1)",
                                      fontFamily: "'Space Grotesk', sans-serif",
                                    }}
                                  >
                                    {topic}
                                  </span>
                                ))}
                              </div>
                            )}

                            {/* Summary text */}
                            <p
                              className="text-gray-300 text-sm mb-2"
                              style={{ fontFamily: "'Space Grotesk', sans-serif" }}
                            >
                              {s.summary || "—"}
                            </p>

                            {/* Caller user ID */}
                            {s.user_id && (
                              <p
                                className="text-xs text-gray-600"
                                style={{ fontFamily: "'Space Grotesk', sans-serif" }}
                              >
                                Caller: {s.user_id}
                              </p>
                            )}
                          </div>
                        );
                      })}
                    </div>
                  )}
                </div>
              </div>

              {/* ── Section 5: Caller Memory ──────────────────────────────── */}
              <div
                className={`rounded-xl backdrop-blur-sm overflow-hidden ${cardBorderClassName}`}
                style={{
                  background: "rgba(28, 28, 37, 0.8)",
                }}
              >
                <div className="p-5">
                  <div className="flex flex-wrap items-center justify-between gap-3 mb-4">
                    <h3
                      className="text-white font-semibold"
                      style={{ fontFamily: "'Space Grotesk', sans-serif" }}
                    >
                      Caller Memory
                    </h3>
                    <button
                      type="button"
                      onClick={() => {
                        setAddMemoryPhone(
                          addMemoryPhone === caller.phone ? null : caller.phone
                        );
                        setNewMemoryKey("");
                        setNewMemoryValue("");
                      }}
                      className="px-4 py-2 rounded-lg text-sm font-semibold text-white cursor-pointer"
                      style={{ background: "linear-gradient(90deg, #8a2be2, #ff6a00)" }}
                    >
                      + Add Memory
                    </button>
                  </div>

                  {addMemoryPhone === caller.phone && (
                    <div
                      className="mb-5 p-4 rounded-xl"
                      style={{
                        background: "rgba(26, 26, 36, 0.75)",
                        border: "1px solid rgba(138, 43, 226, 0.3)",
                      }}
                    >
                      <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 mb-3">
                        <div>
                          <label className="block text-xs text-gray-400 mb-1">Key</label>
                          <input
                            type="text"
                            value={newMemoryKey}
                            onChange={(e) => setNewMemoryKey(e.target.value)}
                            placeholder="e.g. policy_number"
                            className="w-full px-3 py-2 rounded-lg bg-black/40 border border-purple-500/30 text-white text-sm focus:outline-none focus:border-purple-500"
                          />
                        </div>
                        <div>
                          <label className="block text-xs text-gray-400 mb-1">Value</label>
                          <input
                            type="text"
                            value={newMemoryValue}
                            onChange={(e) => setNewMemoryValue(e.target.value)}
                            placeholder="Plain text or JSON"
                            className="w-full px-3 py-2 rounded-lg bg-black/40 border border-purple-500/30 text-white text-sm focus:outline-none focus:border-purple-500"
                          />
                        </div>
                      </div>
                      <div className="flex gap-2">
                        <button
                          type="button"
                          disabled={savingMemory}
                          onClick={() => submitNewMemory(caller.phone)}
                          className="px-4 py-2 rounded-lg text-sm font-semibold text-white cursor-pointer disabled:opacity-50"
                          style={{ background: "linear-gradient(90deg, #8a2be2, #ff6a00)" }}
                        >
                          {savingMemory ? "Saving…" : "Save memory"}
                        </button>
                        <button
                          type="button"
                          onClick={() => setAddMemoryPhone(null)}
                          className="px-4 py-2 rounded-lg text-sm text-gray-300 cursor-pointer"
                          style={{
                            background: "rgba(255,255,255,0.06)",
                            border: "1px solid rgba(138, 43, 226, 0.2)",
                          }}
                        >
                          Cancel
                        </button>
                      </div>
                    </div>
                  )}

                  <div className="space-y-5">
                    {MEMORY_GROUPS.map((group) => {
                      const items = caller.memories.filter(
                        (m) => m.group === group.id
                      );
                      return (
                        <section key={group.id}>
                          <h4
                            className="text-sm font-semibold uppercase tracking-wide mb-3"
                            style={{
                              color: "rgba(196, 181, 253, 0.9)",
                              fontFamily: "'Space Grotesk', sans-serif",
                            }}
                          >
                            {group.label}
                            <span className="text-gray-500 font-normal normal-case ml-2">
                              ({items.length})
                            </span>
                          </h4>
                          {items.length === 0 ? (
                            <p className="text-xs text-gray-500 italic pl-1">
                              No {group.label.toLowerCase()} memories
                            </p>
                          ) : (
                            <div className="space-y-2">
                              {items.map((memory) => (
                                <MemoryRow key={memory.id} memory={memory} />
                              ))}
                            </div>
                          )}
                        </section>
                      );
                    })}
                  </div>
                </div>
              </div>

            </div>
          )}
        </div>
      </PageContainer>
    </CoreLayout>
  );
}
